/**
 * @file DOM 元素或 DOM 事件相关的 hooks
 */
import { useSize as useSizeOriginal, useInViewport as useInViewportOriginal, useEventListener as useEventListenerOriginal, useDocumentVisibility as useDocumentVisibilityOriginal, useKeyPress as useKeyPressOriginal, useHover as useHoverOriginal, } from 'ahooks';
import { useEffect, useMemo, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { noop, throttle } from '../functions/lodash';
/**
 * 监听 DOM 元素是否有鼠标悬停的 hook
 * 文档：https://ahooks.js.org/zh-CN/hooks/use-hover
 *
 * @category DOM Hook
 */
export const useHover = useHoverOriginal;
/**
 * 一个用于判断 dom 元素是否在可视范围之内的 hook.
 * 文档：https://ahooks.js.org/zh-CN/hooks/use-in-viewport/
 *
 * @category DOM Hook
 */
export const useInViewport = useInViewportOriginal;
/**
 * 一个用于获取 dom 元素大小的 hook.
 * 文档: https://ahooks.js.org/zh-CN/hooks/use-size/
 *
 * @category DOM Hook
 */
export const useSize = useSizeOriginal;
/**
 * 监听事件 Hook
 * 文档: https://ahooks.js.org/zh-CN/hooks/use-event-listener
 *
 * @category DOM Hook
 */
export const useEventListener = useEventListenerOriginal;
/**
 * 检查页面是否可见
 * 文档: https://ahooks.js.org/zh-CN/hooks/use-document-visibility
 *
 * @category DOM Hook
 */
export const useDocumentVisibility = useDocumentVisibilityOriginal;
/**
 * 监听 window.resize 事件, 并可为事件的回调函数设定 throttle 的间隔
 *
 * @category DOM Hook
 * @param handler - 回调函数
 * @param wait - throttle 的间隔, 默认 50ms
 */
export const useOnResize = (handler, wait = 50) => {
    const handle = useMemo(() => throttle(handler, wait), [handler, wait]);
    useEffect(() => {
        handle();
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, [handle]);
};
/**
 * 监听键盘事件 Hook
 * 文档: https://ahooks.js.org/zh-CN/hooks/use-key-press
 *
 * @category DOM Hook
 */
export const useKeyPress = useKeyPressOriginal;
/**
 * **重新**聚焦页面时调用函数
 * 通常的使用场景为：
 * 当在页面 A 进行查看或编辑时，需要跳转到页面 B 去完成某个 A 的子内容的编辑，同时在编辑 B 完成后，返回 A 时，需要同步 B 处修改的场景。
 * 此时使用该 Hook 检测与更新数据
 *
 * @category DOM Hook
 * @param handler - handler
 */
export function useDocumentRefocused(handler) {
    const visible = useDocumentVisibility();
    const savedHandler = useRef();
    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);
    const isFirstFocusedRef = useRef(true);
    useEffect(() => {
        var _a;
        if (visible === 'visible') {
            if (isFirstFocusedRef.current) {
                isFirstFocusedRef.current = false;
            }
            else {
                (_a = savedHandler.current) === null || _a === void 0 ? void 0 : _a.call(savedHandler);
            }
        }
    }, [visible]);
}
/**
 * 在组件初次挂载 (mount) 时自动聚焦指定元素
 *
 * @category DOM Hook
 * @returns 返回[应赋给目标元素的 ref, 手动 focus 元素的方法]
 */
export const useAutofocusRef = () => {
    const ref = useRef();
    useEffect(() => {
        var _a, _b;
        (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [ref]);
    const focus = () => {
        var _a, _b;
        (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
    };
    return [ref, focus];
};
/**
 * 监听指定元素的左划/右划/上划/下划事件 (主要用于 web 移动端)
 *
 * 文档见: https://github.com/FormidableLabs/react-swipeable.
 * 一般需要根据实际需求调校 delta 参数 (即滑动多少距离生效)
 *
 * @category DOM Hook
 * @example
 * ```javascript
 * const handlers = useSwipeable({
 *   onSwiped: (eventData) => console.log('Swiped!', eventData),
 *   ...config,
 * })
 * return <div {...handlers}>Swipe here</div>
 * ```
 */
export const useOnSwipe = useSwipeable;
/**
 * 监听指定元素的长按事件 (基于 touch 事件, 只支持移动端)
 *
 * @category DOM Hook
 * @param callback - 回调函数
 * @param options - Options
 * @param options.duration - 要求长按多久? 默认 500ms
 * @param options.stopPropagation - 是否阻止事件冒泡
 * @returns 返回应赋给目标元素的 ref
 *
 * @example
 * ```javascript
 * const ref = useOnLongPressRef<HTMLDivElement>(ev => {
 *   console.log('Long pressed', ev)
 * })
 *
 * return <div ref={ref}>Long Press Me</div>
 * ```
 */
export const useOnLongPressRef = (callback, { duration = 500, stopPropagation = false, } = {}) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current || !callback)
            return noop;
        const el = ref.current;
        let timer = null;
        const onTouchStart = (ev) => {
            if (stopPropagation)
                ev.stopPropagation();
            timer = setTimeout(() => {
                timer = null;
                callback(ev);
            }, duration);
        };
        const onTouchMoveOrEnd = () => {
            clearTimeout(timer);
        };
        el.addEventListener('touchstart', onTouchStart);
        el.addEventListener('touchmove', onTouchMoveOrEnd);
        el.addEventListener('touchend', onTouchMoveOrEnd);
        return () => {
            el === null || el === void 0 ? void 0 : el.removeEventListener('touchstart', onTouchStart);
            el === null || el === void 0 ? void 0 : el.removeEventListener('touchmove', onTouchMoveOrEnd);
            el === null || el === void 0 ? void 0 : el.removeEventListener('touchend', onTouchMoveOrEnd);
        };
    }, [ref, callback, duration, stopPropagation]);
    return ref;
};
//# sourceMappingURL=data:application/json;base64,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